import { findStyleName } from '@/components/shared/generators/generators';

export const MAX_SEARCH_LENGTH = 1000;
export const MAX_STYLE_LENGTH = 50;
export const MAX_FILTER_LENGTH = 10;

export function sanitizeSearchQuery(search: string): string {
  if (!search) return '';

  // Early truncate for extremely long strings before any processing
  if (search.length > MAX_SEARCH_LENGTH * 2) {
    search = search.substring(0, MAX_SEARCH_LENGTH * 2);
  }

  // Remove control characters
  const sanitized = search.replace(/[\x00-\x1F\x7F-\x9F]/g, '');

  // If under max length, return sanitized string
  if (sanitized.length <= MAX_SEARCH_LENGTH) return sanitized;

  // Truncate to nearest word under max length
  const truncated = sanitized.substring(0, MAX_SEARCH_LENGTH);
  const lastBreak = Math.max(truncated.lastIndexOf(' '), truncated.lastIndexOf(','));

  return lastBreak === -1 ? truncated : truncated.substring(0, lastBreak);
}

// Constants for valid filters
export const VALID_FILTERS = new Set(['hot', 'new', 'top1', 'top3', 'top6']);

export function validateSearch(search: string | undefined): string | undefined {
  if (!search) return undefined;
  return sanitizeSearchQuery(search);
}

export function validateFilter(filter: string | undefined): string | undefined {
  if (!filter || filter.length > MAX_FILTER_LENGTH) return undefined;
  return VALID_FILTERS.has(filter) ? filter : undefined;
}

export function validateStyle(style: string | undefined): string | undefined {
  if (!style || style.length > MAX_STYLE_LENGTH) return undefined;
  // TODO: Replace with O(1) lookup
  return findStyleName('en-US', style) ? style : undefined;
}

export function getValidatedParams(searchParams: { [key: string]: string | undefined }) {
  return {
    search: validateSearch(searchParams.search),
    filter: validateFilter(searchParams.filter),
    style: validateStyle(searchParams.style)
  };
}

// Build query string from validated params
export function buildQueryString(params: ReturnType<typeof getValidatedParams>): string {
  const parts: string[] = [];

  if (params.search) {
    parts.push(`search=${encodeURIComponent(params.search)}`);
  }
  if (params.filter) {
    parts.push(`filter=${params.filter}`);
  }
  if (params.style) {
    parts.push(`style=${params.style}`);
  }

  return parts.length ? `?${parts.join('&')}` : '';
}

export function capitalizeWords(str: string): string {
  let result = '';
  let capitalizeNext = true;

  for (let i = 0; i < str.length; i++) {
    if (capitalizeNext && str[i] !== ' ') {
      result += str[i].toUpperCase();
      capitalizeNext = false;
    } else {
      result += str[i];
      capitalizeNext = str[i] === ' ';
    }
  }

  return result;
}
